/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { Asset, AssetTemplate, AssetWithAssignment, VersionedPagination } from "../../utils/Interfaces";

export interface AssetsState {
    existingAssets: AssetWithAssignment[];
    existingAssetsCount: number;
    existingAssetsLoaded: boolean;
    existingAssetsVersion: string;

    existingAssetCatalog: AssetTemplate[];
    existingAssetCatalogCount: number;
    existingAssetCatalogLoaded: boolean;
    existingAssetCatalogVersion: string;

    existingAvailableAssets: Asset[];
    existingAvailableAssetCount: number;
    existingAvailableAssetLoaded: boolean;
    existingAvailableAssetVersion: string;
}

const initialState: AssetsState = {
    existingAssets: [],
    existingAssetsCount: 0,
    existingAssetsLoaded: false,
    existingAssetsVersion: "",

    existingAssetCatalog: [],
    existingAssetCatalogCount: 0,
    existingAssetCatalogLoaded: false,
    existingAssetCatalogVersion: "",

    existingAvailableAssets: [],
    existingAvailableAssetCount: 0,
    existingAvailableAssetLoaded: false,
    existingAvailableAssetVersion: "",
};

export const assetsSlice = createSlice({
    name: "assets",
    initialState,
    reducers: {
        setExistingAssets: (state, action: PayloadAction<VersionedPagination<AssetWithAssignment>>) => {
            state.existingAssets = action.payload.results;
            state.existingAssetsCount = action.payload.count;
            state.existingAssetsLoaded = true;
            state.existingAssetsVersion = action.payload.version;
        },
        setExistingAssetCatalog: (state, action: PayloadAction<VersionedPagination<AssetTemplate>>) => {
            state.existingAssetCatalog = action.payload.results;
            state.existingAssetCatalogCount = action.payload.count;
            state.existingAssetCatalogLoaded = true;
            state.existingAssetCatalogVersion = action.payload.version;
        },
        setExistingAvailableAssets: (state, action: PayloadAction<VersionedPagination<Asset>>) => {
            state.existingAvailableAssets = action.payload.results;
            state.existingAvailableAssetCount = action.payload.count;
            state.existingAvailableAssetLoaded = true;
            state.existingAvailableAssetVersion = action.payload.version;
        },
    },
});

export const { setExistingAssets, setExistingAssetCatalog, setExistingAvailableAssets } = assetsSlice.actions;

export const selectExistingAssets = (state: RootState) => state.assets.existingAssets;
export const selectExistingAssetsCount = (state: RootState) => state.assets.existingAssetsCount;
export const selectExistingAssetsLoaded = (state: RootState) => state.assets.existingAssetsLoaded;
export const selectExistingAssetsVersion = (state: RootState) => state.assets.existingAssetsVersion;

export const selectExistingAssetCatalog = (state: RootState) => state.assets.existingAssetCatalog;
export const selectExistingAssetCatalogCount = (state: RootState) => state.assets.existingAssetCatalogCount;
export const selectExistingAssetCatalogLoaded = (state: RootState) => state.assets.existingAssetCatalogLoaded;
export const selectExistingAssetCatalogVersion = (state: RootState) => state.assets.existingAssetCatalogVersion;

export const selectExistingAvailableAssets = (state: RootState) => state.assets.existingAvailableAssets;
export const selectExistingAvailableAssetsCount = (state: RootState) => state.assets.existingAvailableAssetCount;
export const selectExistingAvailableAssetsLoaded = (state: RootState) => state.assets.existingAvailableAssetLoaded;
export const selectExistingAvailableAssetsVersion = (state: RootState) => state.assets.existingAvailableAssetVersion;

export default assetsSlice.reducer;
